import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import moment from 'moment';
import {
  useFirestoreConnect,
  useFirebase,
  isLoaded,
} from 'react-redux-firebase';
import { useSelector } from 'react-redux';

import { useTheme } from '../../ThemeContext';

import {
  Card,
  CardInner,
  ImageHolder,
  Image,
  Video,
  VideoIcon,
  Placeholder,
  Details,
  Detail,
  DetailLabel,
  Labels,
  Label,
  Selection,
  SelectionInput,
  SelectionCheckmark,
  Icon,
} from './PhotoDocument.style';

const PhotoDocument = ({
  data = {},
  isSelected = false,
  handleClick = () => {},
  handleCheck = () => {},
  handleUncheck = () => {},
  isSmaller = false,
}) => {
  const theme = useTheme();
  const intl = useIntl();
  const firebase = useFirebase();
  const { profile } = useSelector((state) => state.firebase);
  const isProfileCompany = profile.company && profile.company.id ? true : false;
  const [storageVideo, setStorageVideo] = useState(undefined);
  const [storageImage, setStorageImage] = useState(undefined);
  const [storageImageError, setStorageImageError] = useState(false);
  const dataSourcePhotoCategories = useSelector(
    (state) =>
      state.firestore.ordered[
        `data_sources/${data.data_source_id}/photo_categories`
      ]
  );

  // eslint-disable-next-line
  const user = useSelector(
    (state) =>
      data.vinne_annotation &&
      data.vinne_annotation.user_id &&
      state.firestore.data[`users/${data.vinne_annotation.user_id}`]
  );

  const photoDocumentDetails = [
    {
      name: intl.formatMessage({ id: 'PHOTO_DOCUMENT.ORIGIN_ID' }),
      value:
        data.origin_id || intl.formatMessage({ id: 'PHOTO_DOCUMENT.NO_DATA' }),
    },
    {
      name: intl.formatMessage({ id: 'PHOTO_DOCUMENT.DATE' }),
      value: data.creation_date
        ? moment.unix(data.creation_date).format('YYYY-MM-DD hh:mm:ss')
        : intl.formatMessage({ id: 'PHOTO_DOCUMENT.NO_DATA' }),
    },
    {
      name: intl.formatMessage({ id: 'PHOTO_DOCUMENT.USER_NAME' }),
      value: data.vinne_annotation.user_id
        ? intl.formatMessage({
            id: `FILTER.INPUT.VALUE.USER.${data.vinne_annotation.user_id}`,
          })
        : intl.formatMessage({ id: 'PHOTO_DOCUMENT.NO_DATA' }),
    },
  ];

  const firestoreConnect = [];
  if (isProfileCompany && data.data_source_id) {
    firestoreConnect.push({
      collection: 'companies',
      doc: profile.company.id,
      subcollections: [
        {
          collection: 'data_sources',
          doc: data.data_source_id,
          subcollections: [{ collection: 'photo_categories' }],
        },
      ],
      storeAs: `data_sources/${data.data_source_id}/photo_categories`,
    });
  }

  if (data.vinne_annotation && data.vinne_annotation.user_id) {
    firestoreConnect.push({
      collection: 'users',
      doc: data.vinne_annotation.user_id,
      storeAs: `users/${data.vinne_annotation.user_id}`,
    });
  }
  useFirestoreConnect(firestoreConnect);

  useEffect(() => {
    let didCancel = false;
    let didRecall = false;
    let recallId;
    if (data && (data.video_id || data.photo_id)) {
      const loadStorageMedia = async () => {
        try {
          if (data.video_id) {
            const storageVideo = await firebase
              .storage()
              .ref()
              .child(`waste_photos/${data.video_id}.mp4`)
              .getDownloadURL();
            if (!didCancel) setStorageVideo(storageVideo);
          } else if (data.photo_id) {
            const storageImage = await firebase
              .storage()
              .ref()
              .child(`waste_photos_thumbs/${data.photo_id}.jpg`)
              .getDownloadURL();
            if (!didCancel) setStorageImage(storageImage);
          }
        } catch {
          if (!didCancel) {
            if (!didRecall) {
              didRecall = true;
              recallId = setTimeout(() => loadStorageMedia(), 5000);
            } else setStorageImageError(true);
          }
        }
      };
      loadStorageMedia();
    }

    return () => {
      didCancel = true;
      if (storageVideo) setStorageVideo(undefined);
      if (storageImage) setStorageImage(undefined);
      if (storageImageError) setStorageImageError(false);
      if (recallId) clearTimeout(recallId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data && (data.video_id || data.photo_id)]);

  const renderPhotoDocumentDetail = (detail) => {
    return (
      <Detail theme={theme.photoDocument.detail}>
        <DetailLabel theme={theme.photoDocument.detailLabel}>
          {detail.name}:
        </DetailLabel>{' '}
        {detail.value}
      </Detail>
    );
  };

  const renderCardInner = () => {
    return (
      <CardInner>
        <ImageHolder theme={theme.photoDocument.imageHolder}>
          {data.video_id || data.photo_id ? (
            storageVideo ? (
              <>
                <Video
                  theme={theme.photoDocument.video}
                  src={storageVideo}
                  alt={data.video_id}
                  loading='lazy'
                >
                  <source src={storageVideo} type='video/mp4'></source>
                </Video>
                <VideoIcon
                  theme={theme.photoDocument.videoIcon}
                  className='material-icons'
                >
                  play_circle_outline
                </VideoIcon>
              </>
            ) : storageImage ? (
              <Image
                theme={theme.photoDocument.image}
                src={storageImage}
                alt={data.photo_id}
                loading='lazy'
              />
            ) : storageImageError ? (
              <Placeholder className='material-icons'>
                image_not_supported
              </Placeholder>
            ) : (
              ''
            )
          ) : (
            <Placeholder className='material-icons'>
              image_not_supported
            </Placeholder>
          )}
          <Selection
            theme={theme.photoDocument.selection}
            selected={isSelected ? true : undefined}
          >
            <SelectionInput
              readOnly
              type='checkbox'
              defaultChecked={isSelected ? true : undefined}
            />
            <SelectionCheckmark
              theme={theme.photoDocument.selectionCheckmark}
              className='material-icons'
              selected={isSelected ? true : undefined}
            >
              check
            </SelectionCheckmark>
          </Selection>
          <Icon
            theme={theme.photoDocument.icon}
            className='material-icons'
            onClick={(e) => {
              e.stopPropagation();
              handleClick(data.id);
            }}
          >
            open_in_full
          </Icon>
        </ImageHolder>
        <Details>
          {photoDocumentDetails.map((detail, key) => (
            <Fragment key={key}>{renderPhotoDocumentDetail(detail)}</Fragment>
          ))}
        </Details>
        {data.vinne_annotation &&
        data.vinne_annotation.photo &&
        data.vinne_annotation.photo.tags &&
        data.vinne_annotation.photo.tags.length > 0 ? (
          <Labels>
            {data.vinne_annotation.photo.tags.map((mergedTag) => {
              const tag = mergedTag.split('.');
              const category =
                dataSourcePhotoCategories &&
                dataSourcePhotoCategories.find(
                  (category) => category.id === tag[0]
                );
              const tagName =
                category && category.tags && category.tags[tag[1]];

              return (
                <Fragment key={tag[1]}>
                  {isLoaded(dataSourcePhotoCategories) ? (
                    <Label theme={theme.photoDocument.label}>
                      {tagName || tag[1]}
                    </Label>
                  ) : (
                    ''
                  )}
                </Fragment>
              );
            })}
          </Labels>
        ) : (
          ''
        )}
      </CardInner>
    );
  };

  return (
    <Card
      theme={theme.content.card.default}
      onClick={(e) => {
        e.stopPropagation();
        if (isSelected) handleUncheck(data);
        else handleCheck(data);
      }}
      small={isSmaller}
    >
      {renderCardInner()}
    </Card>
  );
};

PhotoDocument.propTypes = {
  data: PropTypes.object,
  isSelected: PropTypes.bool,
  handleClick: PropTypes.func,
  handleCheck: PropTypes.func,
  handleUncheck: PropTypes.func,
  isSmaller: PropTypes.bool,
};

export default PhotoDocument;
